import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';

import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

import { environment } from '@zeusm/environment';
import { GetMeAwareUserStore, isGetMeInProgress, readGetMeStatus, UserStore, waitForGetMeCompletion } from '@zeusm/shared/session/user';

import { AuthStore } from './auth.store';
import { ProfileRouteService } from '../profile/services/profile-route.service';
import { ProfileService } from '../profile/services/profile.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #store = inject(AuthStore);
  readonly #router = inject(Router);
  readonly #profileService = inject(ProfileService);
  readonly #profileRouteService = inject(ProfileRouteService);
  readonly #injector = inject(Injector);

  async signedInCheck(): Promise<true | UrlTree> {
    if (environment.useMocks) {
      this.#store.setAuthStatus(true);
      return true;
    }

    this.#profileService.initializeAuth();

    const userStore = this.#getUserStore();
    if (!userStore) {
      const authorized = this.#store.isAuthorized();
      return authorized ? true : this.#getSignInUrlTree();
    }

    const getMeAwareUserStore = userStore as GetMeAwareUserStore;
    const isAuthorized$ = toObservable(this.#store.isAuthorized, {
      injector: this.#injector,
    });

    try {
      if (userStore.user() && this.#store.isAuthorized()) {
        return true;
      }

      if (isGetMeInProgress(readGetMeStatus(getMeAwareUserStore))) {
        await waitForGetMeCompletion(getMeAwareUserStore, this.#injector);

        if (isGetMeInProgress(readGetMeStatus(getMeAwareUserStore))) {
          const authorized = this.#store.isAuthorized();

          return authorized ? true : this.#getSignInUrlTree();
        }
      }

      await firstValueFrom(
        isAuthorized$.pipe(
          filter(() => {
            const authorized = this.#store.isAuthorized();
            const getMeStatus = readGetMeStatus(getMeAwareUserStore);
            const hasUser = !!userStore.user();

            return authorized || getMeStatus === 'error' || (!hasUser && getMeStatus === 'success');
          }),
          take(1),
          timeout(1000),
        ),
      );
    } catch {
      const authorized = this.#store.isAuthorized();
      return authorized ? true : this.#getSignInUrlTree();
    }

    const authorized = this.#store.isAuthorized();
    return authorized ? true : this.#getSignInUrlTree();
  }

  async signedOutCheck(): Promise<boolean | UrlTree> {
    if (environment.useMocks) {
      this.#store.setAuthStatus(true);
      const userStore = this.#getUserStore();

      return this.#router.parseUrl(this.#profileRouteService.getSignedInTargetRoute(userStore?.user() ?? null));
    }

    this.#profileService.initializeAuth();

    const userStore = this.#getUserStore();

    await waitForGetMeCompletion(userStore as GetMeAwareUserStore | null, this.#injector);

    const authorized = this.#store.isAuthorized();

    if (authorized) {
      return this.#router.parseUrl(this.#profileRouteService.getSignedInTargetRoute(userStore?.user() ?? null));
    }

    return true;
  }

  logout(): void {
    this.#store.logout();
  }

  clearAuthState(): void {
    this.#store.clearAuthState();
  }

  #getUserStore(): InstanceType<typeof UserStore> | null {
    return this.#injector.get(UserStore, null);
  }

  #getSignInUrlTree(): UrlTree {
    const path = window.location.pathname.indexOf('signin') >= 0 ? '' : window.location.pathname;

    return this.#router.parseUrl(!path || path === '/' ? '/signin' : '/signin?url=' + encodeURIComponent(path));
  }
}
